import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Layers } from "lucide-react";
import { HOME_THEME, primaryBtn, sectionPadding } from "../home/theme";
import { getServices } from "./servicesData";
import { translations, LanguageCode } from "../../utils/translations";

const groupServices = (services: ReturnType<typeof getServices>) =>
  services.reduce<Record<string, ReturnType<typeof getServices>>>((acc, s) => {
    acc[s.group] = acc[s.group] || [];
    acc[s.group].push(s);
    return acc;
  }, {});

interface ServicesIndexProps {
  lang?: LanguageCode;
}

const ServicesIndex: React.FC<ServicesIndexProps> = ({ lang = "en" }) => {
  const navigate = useNavigate();
  const content = translations[lang].servicesIndexPage;
  const groups = groupServices(getServices(lang));

  return (
    <>
      {/* HERO */}
      <section
        style={{
          padding: "46px 5% 40px",
          textAlign: "center",
          background: `linear-gradient(160deg, ${HOME_THEME.surfaceAlt} 0%, ${HOME_THEME.surface} 60%)`,
        }}
      >
        <div style={{ maxWidth: "820px", margin: "0 auto" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 14px",
              borderRadius: "999px",
              background: HOME_THEME.primarySoft,
              color: HOME_THEME.primaryDark,
              fontWeight: 800,
              fontSize: "0.75rem",
              letterSpacing: "1px",
              textTransform: "uppercase",
              marginBottom: "14px",
            }}
          >
            <Layers size={15} /> {content.badge}
          </div>

          <h1
            style={{
              fontSize: "2.6rem",
              fontWeight: 800,
              lineHeight: 1.15,
              margin: "0 0 12px 0",
              color: HOME_THEME.heroHeading,
            }}
          >
            {content.heading}
          </h1>

          <p
            style={{
              fontSize: "1.1rem",
              color: HOME_THEME.muted,
              lineHeight: 1.55,
              maxWidth: "660px",
              margin: "0 auto 24px",
            }}
          >
            {content.intro}
          </p>

          <button
            style={primaryBtn}
            onClick={() => {
              const el = document.getElementById("contact");
              if (el) el.scrollIntoView({ behavior: "smooth" });
              else navigate("/#contact");
            }}
          >
            {content.cta}
          </button>
        </div>
      </section>

      {/* GROUPS */}
      {Object.entries(groups).map(([group, items]) => (
        // items = services in this group
        <section
          key={group}
          style={{ padding: sectionPadding, background: HOME_THEME.surface }}
        >
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: 800,
                color: HOME_THEME.heroHeading,
                margin: "0 0 18px 0",
              }}
            >
              {group}
            </h2>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: "16px",
              }}
            >
              {items.map(
                (s) => {
                  const Icon = s.icon;
                  return (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      style={{
                        textDecoration: "none",
                        display: "flex",
                        flexDirection: "column",
                        padding: "20px",
                        borderRadius: "14px",
                        border: `1px solid ${HOME_THEME.border}`,
                        background: HOME_THEME.surfaceAlt,
                      }}
                    >
                      <div
                        style={{
                          width: "42px",
                          height: "42px",
                          borderRadius: "12px",
                          background: HOME_THEME.primarySoft,
                          color: HOME_THEME.primaryDark,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "12px",
                        }}
                      >
                        <Icon size={22} />
                      </div>
                      <h3
                        style={{
                          margin: "0 0 6px 0",
                          fontSize: "1.1rem",
                          fontWeight: 700,
                          color: HOME_THEME.heroHeading,
                        }}
                      >
                        {s.title}
                      </h3>
                      <p
                        style={{
                          margin: "0 0 14px 0",
                          color: HOME_THEME.muted,
                          lineHeight: 1.5,
                          flex: 1,
                        }}
                      >
                        {s.tagline}
                      </p>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          color: HOME_THEME.primary,
                          fontWeight: 700,
                          fontSize: "0.85rem",
                        }}
                      >
                        {content.learnMore} <ArrowRight size={15} />
                      </span>
                    </Link>
                  );
                }
              )}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section style={{ padding: "34px 5%", background: HOME_THEME.surfaceAlt }}>
        <div
          style={{
            maxWidth: "1000px",
            margin: "0 auto",
            padding: "32px 5%",
            textAlign: "center",
            borderRadius: "20px",
            background: `linear-gradient(135deg, ${HOME_THEME.primaryDark}, ${HOME_THEME.primary})`,
            color: "#fff",
            boxShadow: "0 30px 60px -25px rgba(1,126,132,0.6)",
          }}
        >
          <h2 style={{ margin: "0 0 8px 0", fontSize: "1.8rem", fontWeight: 900 }}>
            {content.ctaTitle}
          </h2>
          <p style={{ margin: "0 auto 18px", maxWidth: "560px", opacity: 0.92, lineHeight: 1.5 }}>
            {content.ctaSubtitle}
          </p>
          <Link to="/#contact" style={{ textDecoration: "none" }}>
            <button
              style={{
                padding: "12px 26px",
                background: "#fff",
                border: "none",
                borderRadius: "10px",
                color: HOME_THEME.primaryDark,
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "0.95rem",
              }}
            >
              {content.ctaBtn}
            </button>
          </Link>
        </div>
      </section>
    </>
  );
};

export default ServicesIndex;
